import type { SaasMaterial, SaveSaasMaterialRequest } from '../../types'

export interface PreflightValidationResult {
  valid: boolean
  error?: string
}
export function validateUploadPreflight(
  scope: string,
  fileName: string,
  category?: string
): PreflightValidationResult {
  if (!scope) {
    return { valid: false, error: '请先选择所属业务模块' }
  }
  if (!fileName || !fileName.trim()) {
    return { valid: false, error: '文件名不能为空' }
  }
  if (fileName.trim().length > 200) {
    return { valid: false, error: '文件名长度不能超过 200 个字符' }
  }
  if (category && category.trim().length > 100) {
    return { valid: false, error: '分类名称长度不能超过 100 个字符' }
  }
  return { valid: true }
}

export interface UploadResponseParseResult {
  success: boolean
  data?: {
    materialId: string
    fileName?: string
    objectKey?: string
    fileUrl?: string
    [key: string]: unknown
  }
  error?: string
}

export function isUploadResponseSuccess(json: unknown): boolean {
  if (!json || typeof json !== 'object') return false
  const res = json as { success?: boolean; code?: string; data?: { materialId?: unknown } }
  return res.success === true && res.code === 'OK'
    && typeof res.data?.materialId === 'string' && /^[1-9]\d*$/.test(res.data.materialId)
}

export function parseUploadResponse(json: unknown): UploadResponseParseResult {
  if (!json || typeof json !== 'object') {
    return { success: false, error: '服务端响应数据无效' }
  }
  const res = json as {
    success?: boolean
    code?: string
    message?: string
    data?: { materialId: string; fileName?: string; objectKey?: string; fileUrl?: string }
  }
  if (isUploadResponseSuccess(res)) {
    return {
      success: true,
      data: res.data
    }
  }
  return {
    success: false,
    error: res.message || '上传失败'
  }
}

export function validateSaveMaterialForm(form: SaveSaasMaterialRequest): PreflightValidationResult {
  if (!form.moduleCode) {
    return { valid: false, error: '请选择业务模块' }
  }
  if (!form.name || !form.name.trim()) {
    return { valid: false, error: '素材名称不能为空' }
  }
  if (form.name.trim().length > 200) {
    return { valid: false, error: '素材名称长度不能超过 200 个字符' }
  }
  if (form.category && form.category.trim().length > 100) {
    return { valid: false, error: '分类名称长度不能超过 100 个字符' }
  }
  if (form.type !== 'ARTICLE') {
    const fileUrl = form.fileUrl?.trim()
    if (!fileUrl) {
      return { valid: false, error: '非文稿类型素材必须填写文件 URL' }
    }
    if (!/^https?:\/\//i.test(fileUrl)) {
      return { valid: false, error: '文件 URL 必须以 http:// 或 https:// 开头' }
    }
  }
  if (form.fileSize != null && form.fileSize < 0) {
    return { valid: false, error: '文件大小不能为负数' }
  }
  if (form.durationSeconds != null && form.durationSeconds < 0) {
    return { valid: false, error: '时长不能为负数' }
  }
  if (form.width != null && form.width < 0) {
    return { valid: false, error: '宽度不能为负数' }
  }
  if (form.height != null && form.height < 0) {
    return { valid: false, error: '高度不能为负数' }
  }
  return { valid: true }
}

/**
 * 为文稿加载完整详情。
 * 若文稿正文尚未加载，则必须成功调用详情接口；若加载失败，必须显式抛出异常，绝不回退至缺失正文的列表轻量对象，防止空正文覆盖原数据。
 */
export async function loadArticleDetailForAction(
  material: SaasMaterial,
  fetchDetail: (id: number) => Promise<SaasMaterial>
): Promise<SaasMaterial> {
  if (material.type === 'ARTICLE' && material.content == null) {
    const detail = await fetchDetail(material.id)
    if (!detail) {
      throw new Error('未获取到文稿详情数据')
    }
    return detail
  }
  return material
}

export interface RacingQueryController {
  nextQueryId: () => number
  isLatest: (id: number) => boolean
  currentId: () => number
}

export function createRacingQueryController(): RacingQueryController {
  let currentId = 0
  return {
    nextQueryId: () => ++currentId,
    isLatest: (id: number) => id === currentId,
    currentId: () => currentId
  }
}

export interface AiPollingController {
  start: (id: number) => void
  stop: () => void
  isActive: (id: number) => boolean
  activeId: () => number | null
}

export function createAiPollingController(): AiPollingController {
  let activeId: number | null = null
  return {
    start: (id: number) => { activeId = id },
    stop: () => { activeId = null },
    isActive: (id: number) => activeId === id,
    activeId: () => activeId
  }
}

export interface ReferencesRequestController {
  open: (materialId: number) => number
  close: () => void
  isCurrent: (requestId: number, materialId: number) => boolean
  currentRequestId: () => number
  currentMaterialId: () => number | null
}

export function createReferencesRequestController(): ReferencesRequestController {
  let reqId = 0
  let currentMatId: number | null = null
  return {
    open: (materialId: number) => {
      currentMatId = materialId
      return ++reqId
    },
    close: () => {
      currentMatId = null
      reqId++
    },
    isCurrent: (requestId: number, materialId: number) => {
      return requestId === reqId && currentMatId === materialId
    },
    currentRequestId: () => reqId,
    currentMaterialId: () => currentMatId
  }
}

export function formatDateTime(isoStr?: string | null): string {
  if (!isoStr) return '-'
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return String(isoStr)
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n))
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}
